import { initializeApp, getApps } from "firebase/app";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type MessagePayload,
  type Messaging,
} from "firebase/messaging";
import { notificationService } from "./services/notification.service";
import { logger } from "@/lib";
import { toastService } from "@/shared/toast";

const firebaseConfig = {
  apiKey: "AIzaSyDu7jQ-_q0Ape-zEJu63n4Hg7eEYP9R1Qs",
  authDomain: "andy-stage-7ce15.firebaseapp.com",
  databaseURL: "https://andy-stage-7ce15-default-rtdb.firebaseio.com",
  projectId: "andy-stage-7ce15",
  storageBucket: "andy-stage-7ce15.firebasestorage.app",
  messagingSenderId: "332183980639",
  appId: "1:332183980639:web:df66273ce50986ecea29ea",
  measurementId: "G-2C8XWXD1SJ",
};

const FCM_TOKEN_CACHE_KEY = "fcm_device_token";
const FCM_SW_PATH = "/firebase-messaging-sw.js";

let cachedMessaging: Messaging | null = null;

const getVapidKey = (): string | undefined => {
  return import.meta.env.VITE_FIREBASE_VAPID_KEY;
};

const getMessagingInstance = async (): Promise<Messaging | null> => {
  if (!(await isSupported())) {
    logger.info("Firebase messaging is not supported in this browser.");
    return null;
  }

  if (!getApps().length) {
    initializeApp(firebaseConfig);
  }

  if (!cachedMessaging) {
    cachedMessaging = getMessaging();
  }

  return cachedMessaging;
};

const registerServiceWorker = async (): Promise<ServiceWorkerRegistration> => {
  return navigator.serviceWorker.register(FCM_SW_PATH);
};

const waitForActiveServiceWorker = async (
  registration: ServiceWorkerRegistration
): Promise<ServiceWorkerRegistration> => {
  if (registration.active) return registration;

  await navigator.serviceWorker.ready;

  const refreshed = await navigator.serviceWorker.getRegistration(FCM_SW_PATH);
  if (refreshed?.active) return refreshed;

  throw new Error("Firebase messaging service worker is not active yet.");
};

const saveDeviceToken = async (token: string): Promise<void> => {
  //   const previousToken = localStorage.getItem(FCM_TOKEN_CACHE_KEY);
  //   if (previousToken === token) return;

  await notificationService.registerDevice("web", token);
  localStorage.setItem(FCM_TOKEN_CACHE_KEY, token);
};

const showForegroundToast = (payload: MessagePayload): void => {
  if (document.visibilityState !== "visible") return;

  const title = payload?.notification?.title?.trim();
  const body = payload?.notification?.body?.trim();
  const fallback = payload?.data?.message?.trim();
  const message = [title, body].filter(Boolean).join(" - ") || fallback;

  if (!message) return;
  toastService.show(message, "info");
};

export const setupFirebasePushNotifications = async (): Promise<void> => {
  if (!("Notification" in window) || !("serviceWorker" in navigator)) {
    logger.info("Browser does not support web notifications or service workers.");
    return;
  }

  if (Notification.permission === "denied") {
    logger.info("Notification permission denied by user.");
    return;
  }

  const permission =
    Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (permission !== "granted") return;

  const messaging = await getMessagingInstance();
  if (!messaging) return;

  const vapidKey = getVapidKey();
  if (!vapidKey) {
    logger.warn("Missing VITE_FIREBASE_VAPID_KEY, skipping FCM token generation.");
    return;
  }

  const swRegistration = await registerServiceWorker();
  const activeSwRegistration = await waitForActiveServiceWorker(swRegistration);
  const currentToken = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: activeSwRegistration,
  });

  if (!currentToken) {
    logger.info("No FCM device token received from Firebase.");
    return;
  }

  await saveDeviceToken(currentToken);

  onMessage(messaging, (payload) => {
    logger.info("Foreground push notification received.", payload);
    showForegroundToast(payload);
  });
};
